import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'
import type { CallerId, CallerIdsQueryParams } from '@intone/shared/types'

interface CallerIdsResponse {
  callerIds: CallerId[]
  totalCount: number
  activeCount: number
  offset: number
  limit: number
  campaigns: { id: number; name: string }[]
}

/**
 * Hook to fetch caller IDs with pagination and filtering
 */
export function useCallerIds(params?: CallerIdsQueryParams) {
  const apiClient = useApiClient();

  const queryString = params
    ? '?' +
      new URLSearchParams(
        Object.entries(params)
          .filter(([, v]) => v !== undefined)
          .map(([k, v]) => [k, String(v)])
      ).toString()
    : ''

  return useQuery({
    queryKey: ['callerids', params],
    queryFn: async () => {
      return apiClient<CallerIdsResponse>(`/callerids${queryString}`)
    },
  })
}

/**
 * Hook to update caller ID active status
 */
export function useUpdateCallerIdStatus() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ id, is_active }: { id: number; is_active: boolean }) => {
      return apiClient<{ message: string }>('/callerids/update-status', {
        method: 'POST',
        body: JSON.stringify({ id, is_active }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callerids'] })
    },
  })
}

/**
 * Hook to reset counters for selected caller IDs
 */
export function useBulkResetCounters() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: number[]) => {
      return apiClient<{ message: string }>('/callerids/bulk-reset-counters', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callerids'] })
    },
  })
}

/**
 * Hook to delete selected caller IDs
 */
export function useBulkDeleteCallerIds() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (ids: number[]) => {
      return apiClient<{ message: string }>('/callerids/bulk-delete', {
        method: 'POST',
        body: JSON.stringify({ ids }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callerids'] })
    },
  })
}

/**
 * Hook to move caller IDs to another campaign
 */
export function useBulkMoveCallerIds() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ ids, campaign_id }: { ids: number[]; campaign_id: number | null }) => {
      return apiClient<{ message: string }>('/callerids/bulk-move', {
        method: 'POST',
        body: JSON.stringify({ ids, campaign_id }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callerids'] })
    },
  })
}

/**
 * Hook to upload caller IDs from text input
 */
export function useTextUploadCallerIds() {
  const apiClient = useApiClient();
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      phone_numbers,
      campaign_id,
    }: {
      phone_numbers: string
      campaign_id?: number | null
    }) => {
      return apiClient<{ message: string; added: number; skipped: number }>('/callerids/text-upload', {
        method: 'POST',
        body: JSON.stringify({ phone_numbers, campaign_id }),
      })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['callerids'] })
    },
  })
}